
import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

export interface EmailSettings {
  fromAddress: string;
  enabled: boolean
}

const defaultSettings: EmailSettings = {
  fromAddress: 'NationCard™ <sender@notifications.nationcard.org>',
  enabled: false
};

export const useEmailSettings = () => {
  const [settings, setSettings] = useState<EmailSettings>(defaultSettings);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data: fromAddressData, error: fromAddressError } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'email_from_address')
          .maybeSingle();
        
        if (fromAddressError) {
          console.error('Error fetching email settings:', fromAddressError);
          return;
        }
        
        const updatedSettings = { ...defaultSettings };
        
        if (fromAddressData?.value) {
          updatedSettings.fromAddress = fromAddressData.value as string;
        }
        
        setSettings(updatedSettings);
      } catch (error) {
        console.error('Unexpected error in fetchSettings:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchSettings();
  }, []);

  const handleInputChange = (key: keyof EmailSettings, value: string | boolean) => {
    setSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };
  
  const saveSettings = async () => {
    try {
      const { error: fromAddressError } = await supabase
        .from('app_settings')
        .upsert({
          key: 'email_from_address',
          value: settings.fromAddress || defaultSettings.fromAddress
        }, {
          onConflict: 'key'
        });
      
      if (fromAddressError) {
        throw fromAddressError;
      }
      
      return { success: true };
    } catch (error: any) {
      console.error('Error saving email settings:', error);
      return { success: false, error: error.message };
    }
  };
  
  return {
    settings,
    isLoading,
    handleInputChange,
    saveSettings
  };
};
